import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Card, CardContent } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { useToast } from 'components/ui/use-toast';
import {
  aiSettings,
  type AIFeatureSetting,
  type AIModelCatalog as AIModelCatalogData,
  type AIUsageSummary,
} from 'services/api/aiSettingsClient';
import ElevenLabsRoleplayVoicesCard from '../ElevenLabsRoleplayVoicesCard';
import VoiceProcessingModelsCard from '../VoiceProcessingModelsCard';
import VoiceRuntimeSettingsCard from '../VoiceRuntimeSettingsCard';
import AIModelCatalog from './AIModelCatalog';
import { AIUsageKpis, AIUsageTables } from './AIUsageSummary';
import { ManagedSettingsGroups, groupSettings, summarizeSetting } from './ManagedSettingsList';
import { ModuleVoiceTranscriptionModelCard, RoleplayVoiceModelCard, VoiceRealtimeModelCard } from './VoiceModelCards';

const TABS = ['features', 'voice', 'models', 'usage'] as const;
type TabValue = (typeof TABS)[number];

const EMPTY_CATALOG: AIModelCatalogData = {
  models: [],
  fetched_at: null,
  last_error: '',
  last_error_at: null,
  api_key_configured: false,
};

const AISettings: React.FC = () => {
  const { addToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabValue = TABS.includes(tabParam as TabValue) ? (tabParam as TabValue) : 'features';
  const [features, setFeatures] = useState<AIFeatureSetting[] | null>(null);
  const [catalog, setCatalog] = useState<AIModelCatalogData>(EMPTY_CATALOG);
  const [usage, setUsage] = useState<AIUsageSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUsage, setIsLoadingUsage] = useState(true);
  const [isRefreshingCatalog, setIsRefreshingCatalog] = useState(false);
  const [openFeature, setOpenFeature] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const refreshCatalog = useCallback(async (quiet = false) => {
    setIsRefreshingCatalog(true);
    try {
      const response = await aiSettings.refreshModels();
      setCatalog(response.data);
      if (!quiet) {
        addToast(response.data.last_error
          ? { title: 'Couldn\'t refresh the model list', description: response.data.last_error, variant: 'destructive' }
          : { title: 'Model list refreshed', description: `${response.data.models.length} models available.` });
      }
    } catch {
      if (!quiet) addToast({ title: 'Couldn\'t refresh the model list', variant: 'destructive' });
    } finally {
      setIsRefreshingCatalog(false);
    }
  }, [addToast]);

  useEffect(() => {
    let isMounted = true;
    aiSettings.getOverview()
      .then((response) => {
        if (!isMounted) return;
        setFeatures(response.data.features);
        setCatalog(response.data.catalog);
        if (!response.data.catalog.fetched_at && response.data.catalog.api_key_configured) {
          void refreshCatalog(true);
        }
      })
      .catch(() => {
        if (isMounted) addToast({ title: 'Failed to load AI settings', description: 'Please refresh and try again.', variant: 'destructive' });
      })
      .finally(() => { if (isMounted) setIsLoading(false); });
    aiSettings.getUsage()
      .then((response) => { if (isMounted) setUsage(response.data); })
      .catch(() => { /* Usage is optional; the cards show it as unavailable. */ })
      .finally(() => { if (isMounted) setIsLoadingUsage(false); });
    return () => { isMounted = false; };
  }, [addToast, refreshCatalog]);

  const handleSaved = useCallback((next: AIFeatureSetting) => {
    setFeatures((current) => current?.map((feature) => (feature.key === next.key ? next : feature)) ?? current);
  }, []);

  const groups = useMemo(() => {
    const term = query.trim().toLowerCase();
    const matches = (features ?? []).filter((feature) => !term || [
      feature.label, feature.description, feature.group, summarizeSetting(feature),
    ].some((text) => text.toLowerCase().includes(term)));
    return groupSettings(matches);
  }, [features, query]);

  const customCount = features?.filter((feature) => Object.keys(feature.overrides).length > 0).length ?? 0;

  return (
    <section className="space-y-6 px-6 py-8" data-testid="ai-settings-page">
      <div className="max-w-3xl space-y-2">
        <h1 className="text-3xl font-semibold text-foreground">AI Settings</h1>
        <p className="text-sm text-muted-foreground">
          Models, prompts, and voices for every AI feature. Saved changes apply to new requests right away.
        </p>
      </div>

      <AIUsageKpis usage={usage} isLoading={isLoadingUsage} />

      <Tabs
        value={activeTab}
        onValueChange={(value) => setSearchParams((params) => {
          const next = new URLSearchParams(params);
          next.set('tab', value);
          return next;
        }, { replace: true })}
      >
        <TabsList className="justify-start gap-4 bg-transparent px-0">
          <TabsTrigger value="features" className="px-2">Features</TabsTrigger>
          <TabsTrigger value="voice" className="px-2">Voice</TabsTrigger>
          <TabsTrigger value="models" className="px-2">Models</TabsTrigger>
          <TabsTrigger value="usage" className="px-2">Usage</TabsTrigger>
        </TabsList>

        <TabsContent value="features" className="space-y-6">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading AI settings...</p>
          ) : features ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Input
                  aria-label="Search features"
                  placeholder="Search features or models"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  className="max-w-sm"
                />
                <p className="text-sm text-muted-foreground">{`${customCount} of ${features.length} customized`}</p>
              </div>
              {groups.length ? (
                <ManagedSettingsGroups
                  groups={groups}
                  catalog={catalog}
                  openFeature={openFeature}
                  onToggle={(key) => setOpenFeature((current) => (current === key ? null : key))}
                  onSaved={handleSaved}
                />
              ) : <p className="text-sm text-muted-foreground">No features match.</p>}
            </>
          ) : (
            <Card className="w-full max-w-none" maxWidth="none" disableAnimation>
              <CardContent className="py-5">
                <p className="text-sm text-muted-foreground">AI settings are unavailable.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="voice" className="space-y-6">
          <VoiceRuntimeSettingsCard />
          <VoiceProcessingModelsCard />
          <VoiceRealtimeModelCard />
          <ModuleVoiceTranscriptionModelCard />
          <RoleplayVoiceModelCard />
          <ElevenLabsRoleplayVoicesCard />
        </TabsContent>

        <TabsContent value="models">
          <AIModelCatalog catalog={catalog} isRefreshing={isRefreshingCatalog} onRefresh={() => { void refreshCatalog(); }} />
        </TabsContent>

        <TabsContent value="usage">
          <AIUsageTables usage={usage} isLoading={isLoadingUsage} />
        </TabsContent>
      </Tabs>
    </section>
  );
};

export default AISettings;
